import { useCallback, useState } from "react";
import SettingsRepository from "@/utilities/repositories/Settings";
import { E_LANDING_PAGE_TYPE } from "@/utilities/types/LandingPage";
import { DOMAIN_UNIQUENESS_CHECK_DEBOUNCE } from "../../config";
import { debounce } from "lodash";
import useProfile from "@/hooks/use-profile";

interface UseDomainManagementProps {
  projectId?: string;
  suffixDomains: string[];
}

/**
 * Custom hook to manage all domain-related operations
 */
export function useDomainManagement({
  projectId,
  suffixDomains,
}: UseDomainManagementProps) {
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { profile } = useProfile();

  /**
   * Check if domain is available
   */
  const checkDomainUniqueness = useCallback(
    async (
      domain: string,
      type: E_LANDING_PAGE_TYPE,
      suffix?: string
    ) => {
      try {
        setIsCheckingDomain(true);
        const fullDomain =
          type === E_LANDING_PAGE_TYPE.SUBDOMAIN
            ? `${domain}${suffix || suffixDomains[0]}`
            : domain;

        const response =
          await SettingsRepository.checkAvailableDomain(fullDomain);
        return !response.data; // true = domain is unique, false = domain is taken
      } catch (error) {
        console.error("Error checking domain availability:", error);
        return false;
      } finally {
        setIsCheckingDomain(false);
      }
    },
    [suffixDomains]
  );

  /**
   * Generate a subdomain automatically
   */
  const generateSubdomain = useCallback(async () => {
    if (!projectId) return null;

    setIsGenerating(true);
    try {
      const response = await SettingsRepository.generateUniqeDomain(projectId);
      const randomText = Math.random().toString(36).substring(7);
      const subDomain = response?.data
        ? `${randomText}-${response.data}`
        : randomText;

      return subDomain;
    } catch (error) {
      console.error("Error generating subdomain:", error);
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [projectId]);

  /**
   * Generate CName target domain automatically
   */
  const generateCnameTargetDomain = useCallback(async () => {
    try {
      const response = await SettingsRepository.generateUniqueCNameDomain({
        userId: profile?.id || "",
        projectId: projectId || "",
      });

      return response?.data || "";
    } catch (error) {
      console.error("Error generating cname target domain:", error);
      return "";
    }
  }, [profile?.id, projectId]);

  const generateCnameTargetDomains = useCallback(async () => {
    try {
      const response = await SettingsRepository.generateUniqueCNameDomains({
        userId: profile?.id || "",
        projectId: projectId || "",
      });

      return response?.data || { cNameTargetDomain: "", cNameTarget: "" };
    } catch (error) {
      console.error("Error generating cname target domain:", error);
      return { cNameTargetDomain: "", cNameTarget: "" };
    }
  }, [profile?.id, projectId]);

  /**
   * Debounced function to check domain uniqueness
   */
  const debouncedCheckDomain = useCallback(
    debounce(
      async (
        domain: string,
        type: E_LANDING_PAGE_TYPE,
        onError: (message: string) => void,
        suffix?: string
      ) => {
        if (!domain) return;

        const isUnique = await checkDomainUniqueness(domain, type, suffix);
        if (!isUnique) {
          onError("This domain is already taken");
        }
      },
      DOMAIN_UNIQUENESS_CHECK_DEBOUNCE
    ),
    [checkDomainUniqueness]
  );

  return {
    isCheckingDomain,
    isGenerating,
    checkDomainUniqueness,
    generateSubdomain,
    generateCnameTargetDomain,
    generateCnameTargetDomains,
    debouncedCheckDomain,
  };
}
